import { SITES, type SiteId } from '@goodbuild/rules/sites';
import brand from './brand.module.css';
import styles from './boot.module.css';
import { LOGO_BARS } from './strip';

/**
 * The screen the product boots into (#135).
 *
 * **The session check runs on every single page load**, and until it answers
 * the application cannot draw anything true: it does not know whether there is
 * a person, what they are called, or which of the three surfaces they are
 * entitled to. #100 replaced the empty rectangle that used to sit in that gap
 * with the mark waiting, which is the right answer for the two hundred
 * milliseconds it usually takes.
 *
 * It is not the right answer for the times it takes two seconds. A logo
 * pulsing in the middle of an empty window for that long is the same picture as
 * a page that has hung, and the person watching it has nothing to do and
 * nothing to read.
 *
 * So this is the other thing a wait can be, and games have known what that is
 * for thirty years: the screen is *part of the product* while it lasts. The
 * mark and the wordmark say whose it is, a line of type says what is happening,
 * a bar sweeps along the bottom edge, and a tip tells the person something
 * about GOODBUILD they might not know. The wait stops being dead time.
 *
 * **The mark waits and the bar sweeps, and they are doing different jobs.** For
 * a while the lockup was held still here, on the argument that two moving
 * things on an empty screen compete — which was true while the mark had no
 * animation of its own and the sweep was the only motion there was. It has one:
 * a light running up the bars, slow, with a long rest in it.
 *
 * They sit at opposite ends of the screen and say different things. The mark
 * breathing is *this product is here*; the bar is *something is happening*. The
 * one thing that would make them compete is if either implied progress, and
 * neither can — the strip's heights never move (see `.loaderStrip` and
 * `gb-loader-run` in `brand.module.css`) and the sweep never fills (see
 * `.bar`).
 *
 * ## What is deliberately not here
 *
 * **A percentage, and any bar that fills.** There is exactly one round trip
 * happening and no way to measure how far through it we are, so every number
 * this screen could show would be invented. The bar sweeps rather than fills —
 * see `.bar` in the stylesheet, which is where section 03's rule about the
 * progress strip is argued out.
 *
 * **Artwork.** A games loading screen is usually a splash image, and this one
 * is type, the mark and a rule, because that is what the brand is. It also has
 * to be on screen inside 140ms on a cold load, which is not a budget an image
 * fits in.
 *
 * ## Timing lives with the caller
 *
 * Nothing here decides when it appears, how long a tip stays, or which tip it
 * is. This component renders what it is given; `apps/app/src/loading.tsx` owns
 * the three thresholds, because they are facts about *waiting* rather than
 * about this screen, and `useSlow` was already there holding two of them.
 *
 * `tip` being optional is the load-bearing part of that arrangement: the same
 * screen, with the tip left out, is what a short wait gets.
 */
export function BootScreen({
  space,
  label = 'Loading',
  tip,
}: {
  /**
   * Whose colours, and whose name under the mark.
   *
   * Required, exactly as on `PageLoader` and for the same reason: this renders
   * outside `Page`, so there is no `[data-gb-space]` above it to inherit from
   * and the fallback would be `:root` — the store's — which is how a studio
   * ends up watching a warm orange boot screen on the way into its own blue
   * portal.
   */
  space: SiteId;
  /** What is being waited for. Announced, so write it for a person. */
  label?: string;
  /**
   * The tip currently showing, if the wait has lasted long enough to read one.
   *
   * Undefined is a real state and the common one — most session checks never
   * get here. The band and its rule go with it, so a short wait is the mark and
   * the wordmark on clean ground with the bar along the bottom.
   *
   * **What does not go with it is the room the band takes.** The footer holds
   * its height whether or not there is a tip in it, so the mark is centred in
   * the same space for the whole wait — see `.foot`. A tip that arrived into a
   * layout sized for no tip would shove the mark upward at the 900ms mark,
   * which is a jolt on the one screen whose entire job is to be calm while
   * somebody waits.
   */
  tip?: string;
}): React.ReactElement {
  return (
    <div className={styles.boot} data-gb-space={space}>
      {/*
        Two rows, and the wrapper is what makes them two.

        The screen is exactly the viewport and never scrolls — see `.boot` — so
        the mark has to take *what is left* after the footer rather than a
        measure somebody guessed. That is a grid track, and a grid track needs
        one element to be.
      */}
      <div className={styles.hero}>
        <div className={styles.lockup}>
          {/*
            The mark, waiting, built here rather than borrowed from `Loader`.

            `Loader` is a row — mark then label — and this screen wants the two
            on separate grid rows at sizes it decides. What it must not do is
            invent a second set of bars: `LOGO_BARS` is the one statement of
            what the logo's heights are, and `loaderStrip` and `loaderLg` are
            the same classes the loader uses, so the boot mark and every other
            waiting mark in the product are one drawing.
          */}
          <span
            className={`${brand.loaderStrip} ${brand.loaderIdent} ${brand.loaderLg}`}
            aria-hidden="true"
          >
            {LOGO_BARS.map((height) => (
              <i key={height} style={{ height: `${height}%` }} />
            ))}
          </span>
          {/*
            `wordLockup` and `word` come from `brand.module.css` rather than
            being restated here, so the wordmark on the boot screen is character
            for character the wordmark in the navigation bar. Only the size is
            this file's, and only because `Logo` parameterises that too.

            The site label is dropped on the store, matching `Logo`: `GOODBUILD
            Store` is not a thing anybody calls it, and the apex is the default
            surface rather than one of the named ones.
          */}
          <span className={brand.wordLockup}>
            <span className={`${brand.word} ${styles.word}`}>Goodbuild</span>
            {space !== 'store' && (
              <span className={brand.wordSite}>{SITES[space].switcherLabel}</span>
            )}
          </span>
        </div>

        {/*
          The one thing on this screen a screen reader is told about. The mark
          is `aria-hidden`, the tip is readable but not announced (see below),
          and this is the sentence that says what is actually going on.
        */}
        <p className={styles.status} role="status">
          {label}
        </p>
      </div>

      <div className={styles.foot}>
        {tip ? (
          <div className={styles.tipBand}>
            {/*
              `key` is the tip, so React replaces the paragraph rather than
              editing its text when the rotation comes round — which is what
              makes the entrance animation play again for each one. Two
              identical tips in a row would not re-enter; the pools have no
              duplicates, and a duplicate tip is a copy bug either way.

              No `aria-live`. A region that announced itself would interrupt a
              screen reader every few seconds with something nobody asked for,
              on a screen whose actual news is one line above. The tip is
              readable on demand and silent otherwise, which is what a tip is.
            */}
            <p className={styles.tip} key={tip}>
              <span className={styles.tipLabel}>Pro tip</span>
              <span className={styles.tipText}>{tip}</span>
            </p>
          </div>
        ) : null}

        {/*
          Decorative in the strict sense: it carries no information at all, not
          even a rough one, because there is none to carry. The status line
          above says everything true about this wait.
        */}
        <div className={styles.bar} aria-hidden="true">
          <i />
        </div>
      </div>
    </div>
  );
}
